// FeaturedWindows.jsx
// The landing page's "peek through real windows" section: live thumbnails of
// real public gardeners' windows, rendered with the shared WindowScene.

// Packages, Dependencies, and Shared Libraries
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { pickFeaturedWindows } from "../DatabaseOperations";

// React Components
import WindowScene, {
  DEFAULT_ROOM,
  computeWindowPlacement,
} from "../WindowPage/WindowScene";

/*******************************************************************************
 * @func FeaturedWindowCard (React Component)
 * One clickable window thumbnail: the gardener's first room with their plants
 * placed, captioned with their name. Clicking opens the full window page.
 *
 * @param featured (object): { userId, user, plants, speciesMap } from
 * pickFeaturedWindows
 * @return JSX Element
 *
 * @notes The scene is non-interactive here — the whole card is the link
 *
 ******************************************************************************/
const FeaturedWindowCard = ({ featured }) => {
  const navigate = useNavigate();
  const { userId, user, plants, speciesMap } = featured;

  const rooms =
    Array.isArray(user?.rooms) && user.rooms.length > 0
      ? user.rooms
      : [DEFAULT_ROOM];

  const { placedPlants } = useMemo(
    () => computeWindowPlacement(plants, speciesMap, rooms[0], rooms),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plants, speciesMap]
  );

  const name = user.name || "A gardener";

  return (
    <div
      className="cursor-pointer group w-[240px] shrink-0"
      onClick={() => navigate("/window/" + userId)}
    >
      <div className="pointer-events-none transition-transform group-hover:-translate-y-1">
        <WindowScene
          room={rooms[0]}
          plants={placedPlants}
          speciesMap={speciesMap}
          interactive={false}
        />
      </div>
      <div className="text-center mt-2 font-bold text-leaf-green-dark text-sm group-hover:underline underline-offset-4">
        {name}'s window →
      </div>
    </div>
  );
}; // End FeaturedWindowCard

/*******************************************************************************
 * @func FeaturedWindows (React Component)
 * Fetches a few public windows and lays their thumbnails out in a row. The
 * whole section renders nothing until (and unless) real windows are found, so
 * the landing page never shows an empty shell.
 *
 * @param None
 * @return JSX Element | null
 *
 * @notes None
 *
 ******************************************************************************/
const FeaturedWindows = () => {
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    let isMounted = true;
    pickFeaturedWindows(3).then((windows) => {
      if (isMounted) setFeatured(windows);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  if (!featured || featured.length === 0) return null;

  return (
    <section className="bg-body-bg border-y border-leaf-brown/10 py-14">
      <div className="mx-auto max-w-5xl px-4">
        <h2 className="text-center font-header text-3xl text-leaf-green-dark mb-2 pb-1">
          Peek through real windows
        </h2>
        <p className="text-center text-sm text-leaf-brown-soft mb-8 max-w-xl mx-auto">
          These are live windows from real Leaflings gardeners — arranged in
          the app, growing right now. Tap one to visit.
        </p>
        <div className="flex justify-center gap-6 flex-wrap">
          {featured.map((f) => (
            <FeaturedWindowCard key={f.userId} featured={f} />
          ))}
        </div>
      </div>
    </section>
  );
}; // End FeaturedWindows

export default FeaturedWindows;
